import * as React from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import type { useUpdateDutyFirstMessage } from '@/hooks/useDutyDetail'

/**
 * The single confirm-and-clear flow for a duty's saved first message, shared
 * by both clear paths (the card's Clear button and the tools-popover toggle)
 * so copy, toasts, and pending behavior can't drift apart.
 *
 * The dialog owns the whole flow — strings, try/catch, both toasts, and
 * close-on-success (a failure leaves it open with an error toast so the
 * operator can retry). The first-message *mutation instance* is passed in by
 * the caller rather than created here: TanStack mutation state is per hook
 * instance, and each caller needs the same instance's `isPending` for its own
 * UI (the provider aggregates it into `isToolMutationPending`; the card shares
 * it with Save/Clear disabling).
 *
 * Usage: the provider drives it controlled via `open`/`onOpenChange`; the card
 * passes its Clear button as `trigger` and lets the dialog manage open state.
 */
export function ClearFirstMessageDialog({
  mutation,
  onCleared,
  open,
  onOpenChange,
  trigger,
}: {
  /** The caller's `useUpdateDutyFirstMessage(dutyId)` instance. */
  mutation: ReturnType<typeof useUpdateDutyFirstMessage>
  /** Runs after a successful clear (e.g. collapse the card's editor). */
  onCleared?: () => void
  /** Controlled mode — pass both `open` and `onOpenChange`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Uncontrolled mode — rendered as the dialog's trigger (`asChild`). */
  trigger?: React.ReactNode
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const isOpen = open ?? uncontrolledOpen
  const setOpen = onOpenChange ?? setUncontrolledOpen
  const isPending = mutation.isPending

  const handleConfirm = async () => {
    try {
      // Resolves after the mutation's awaited query invalidation, so the
      // server-derived tools state is already refreshed when the dialog
      // closes — no local visibility override needed.
      await mutation.mutateAsync(null)
      setOpen(false)
      onCleared?.()
      toast.success('First message cleared')
    } catch (err) {
      // Leave the dialog open so there's something to retry from.
      toast.error(
        err instanceof Error ? err.message : 'Failed to clear first message'
      )
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setOpen}>
      {trigger !== undefined && (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      )}
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Clear first message?</AlertDialogTitle>
          <AlertDialogDescription>
            Angelica will fall back to the default opening. This can't be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={(e) => {
              // Radix's Action closes synchronously on click; prevent that so
              // the dialog stays up (disabled) while the PATCH runs and only
              // closes on success.
              e.preventDefault()
              void handleConfirm()
            }}>
            Clear message
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
